import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X } from 'lucide-react';

interface Sheet {
  id: string;
  name: string;
  data: any[];
  columns: any[];
}

interface SheetTabsProps {
  sheets: Sheet[];
  activeSheetId: string;
  onSheetSelect: (sheetId: string) => void;
  onSheetAdd: () => void;
  onSheetDelete: (sheetId: string) => void;
  onSheetRename: (sheetId: string, newName: string) => void;
  className?: string;
}

export const SheetTabs: React.FC<SheetTabsProps> = ({
  sheets,
  activeSheetId,
  onSheetSelect,
  onSheetAdd,
  onSheetDelete,
  onSheetRename,
  className = ''
}) => {
  const [editingSheet, setEditingSheet] = React.useState<string | null>(null);
  const [editName, setEditName] = React.useState('');

  const handleTabDoubleClick = (sheet: Sheet) => {
    setEditingSheet(sheet.id);
    setEditName(sheet.name);
  };

  const handleNameSubmit = () => {
    if (editingSheet && editName.trim()) {
      onSheetRename(editingSheet, editName.trim());
    }
    setEditingSheet(null);
    setEditName('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSubmit();
    } else if (e.key === 'Escape') {
      setEditingSheet(null);
      setEditName('');
    }
  };

  return (
    <div className={`sheet-tabs flex items-center bg-gray-800 border-t border-gray-600 ${className}`}>
      <div className="flex items-center overflow-x-auto">
        <AnimatePresence>
          {sheets.map((sheet) => (
            <motion.div
              key={sheet.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className={`
                flex items-center px-3 py-2 border-r border-gray-600 cursor-pointer group
                ${activeSheetId === sheet.id 
                  ? 'bg-gray-700 text-white border-b-2 border-blue-500/50' 
                  : 'text-slate-300 hover:bg-slate-700/50'
                }
              `}
              onClick={() => onSheetSelect(sheet.id)}
              onDoubleClick={() => handleTabDoubleClick(sheet)}
            >
              {editingSheet === sheet.id ? (
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onBlur={handleNameSubmit}
                  onKeyDown={handleKeyDown}
                  className="bg-gray-600 text-white px-1 py-0.5 text-sm rounded border-none outline-none"
                  autoFocus
                />
              ) : (
                <>
                  <span className="text-sm font-medium">{sheet.name}</span>
                  {sheets.length > 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSheetDelete(sheet.id);
                      }}
                      className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-red-600 rounded"
                    >
                      <X size={12} />
                    </button>
                  )}
                </>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
        
        <button
          onClick={onSheetAdd}
          className="flex items-center px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-700/50 transition-colors"
          title="Add new sheet"
        >
          <Plus size={16} />
        </button>
      </div>
    </div>
  );
};
